import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, XCircle, XOctagon } from 'react-feather';
import { api, Question } from '../api/client';
import { useSnackbar } from '../context/SnackbarContext';

interface Props {
  modal?: boolean;
  onClose?: () => void;
  onCreated?: (question: Question) => void;
}

const CreateQuestion: React.FC<Props> = ({ modal = false, onClose, onCreated }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      showSnackbar('Title is required.', { variant: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const question = await api.createQuestion({
        title: title.trim(),
        description: description.trim() || undefined,
        is_public: isPublic,
      });
      showSnackbar('Question created.', { variant: 'success' });
      if (onCreated) {
        onCreated(question);
      } else {
        navigate(`/questions/${question.id}`);
      }
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to create question', { variant: 'error' });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate('/');
  };

  const form = (
      <form onSubmit={handleSubmit} className={modal ? 'modal-form' : 'question-form'}>
        <div className="form-group">
          <label htmlFor="title">Question Title *</label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., What food gives my dog allergies?"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional details about your question..."
            rows={4}
          />
        </div>

        <div className="form-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            <span>Make this question public</span>
          </label>
          <small>Allow multiple users to contribute data points</small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={submitting}
          >
            <XOctagon size={14} />
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            <Save size={14} />
            {submitting ? 'Creating...' : 'Create question'}
          </button>
        </div>
      </form>
  );

  if (modal) {
    return (
      <div className="modal-overlay" onClick={handleCancel}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create a new question</h2>
            <button className="btn-close" onClick={handleCancel} disabled={submitting}>
              <XCircle size={18} />
            </button>
          </div>
          {form}
        </div>
      </div>
    );
  }

  return (
    <div className="create-question container">
      <h2>Create a new question</h2>
      {form}
    </div>
  );
};

export default CreateQuestion;
